import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  RECLASSIFY_BY_ACCOUNT_MAX_APPLY,
  type Account,
  type BankAccount,
  type ReclassifyByAccountResult,
  type ReclassifyPayrollTipsResult,
} from '@accounting/shared';
import { useAccounts } from '../../hooks/useAccounts';
import { useBankAccounts, useReclassifyByAccount } from '../../hooks/useBank';
import { useReclassifyPayrollTips } from '../../hooks/usePayroll';
import { useVerifyChain } from '../../hooks/useReconciliation';
import { AccountPicker } from '../../components/accounts/AccountPicker';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';
import { ApiError } from '../../lib/api';

export const Route = createFileRoute('/_auth/reallocations')({
  component: ReallocationsPage,
});

const TIPS_DEFAULT_CODE = '2025';

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | null)?.error ?? err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

function isPostable(a: Account): boolean {
  return a.isActive && a.allowDirectPosting;
}

function ReallocationsPage() {
  const companyId = useActiveCompanyId();
  const { data: accounts = [] } = useAccounts(companyId);
  const { data: banks } = useBankAccounts(companyId);
  const bankAccounts = banks?.bankAccounts ?? [];

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, margin: '8px 0 6px' }}>Reallocations</h1>
      <p className="muted" style={{ marginTop: 0, fontSize: 13, maxWidth: 760 }}>
        Move posted amounts between accounts in bulk without touching the bank side or any
        reconciliation. Every panel previews first and writes nothing until you apply.
      </p>

      <HashChainPanel companyId={companyId} bankAccounts={bankAccounts} />
      <DepositsAndPaymentsPanel companyId={companyId} accounts={accounts} />
      <PayrollTipsPanel companyId={companyId} accounts={accounts} />
    </div>
  );
}

// ── Hash chain ──────────────────────────────────────────────────────────────

function HashChainPanel({
  companyId,
  bankAccounts,
}: {
  companyId: string;
  bankAccounts: BankAccount[];
}) {
  return (
    <div className="card" style={{ padding: 16, marginBottom: 12, maxWidth: 960 }}>
      <h2 style={{ fontSize: 14, marginTop: 0, marginBottom: 6 }}>Hash chain</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
        Each bank account's reconciliation chain, recomputed now. Check it before and after
        applying anything below. Nothing on this page should ever change it.
      </p>
      {bankAccounts.length === 0 && <p className="muted" style={{ fontSize: 13 }}>No bank accounts.</p>}
      {bankAccounts.map((b) => (
        <HashChainRow key={b.id} companyId={companyId} bank={b} />
      ))}
    </div>
  );
}

function HashChainRow({ companyId, bank }: { companyId: string; bank: BankAccount }) {
  const { data, isLoading, error, refetch, isFetching } = useVerifyChain(companyId, bank.id);
  const label = `${bank.institutionName} ${bank.accountName}`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '4px 0' }}>
      <span style={{ minWidth: 260 }}>{label}</span>
      {isLoading && <span className="muted">Checking</span>}
      {error && <span className="tag">error: {errorText(error)}</span>}
      {data && (
        <span className={data.valid ? 'tag ok' : 'tag'}>
          {data.valid ? 'valid' : 'INVALID'} ({data.entries.length} link{data.entries.length === 1 ? '' : 's'})
        </span>
      )}
      <button type="button" className="btn-secondary" onClick={() => refetch()} disabled={isFetching}>
        {isFetching ? 'Checking' : 'Re-check'}
      </button>
    </div>
  );
}

// ── Deposits and payments ───────────────────────────────────────────────────

function DepositsAndPaymentsPanel({ companyId, accounts }: { companyId: string; accounts: Account[] }) {
  const run = useReclassifyByAccount(companyId);
  const [fromAccountId, setFrom] = useState('');
  const [toAccountId, setTo] = useState('');
  const [from, setFromDate] = useState('');
  const [to, setToDate] = useState('');
  const [descriptionContains, setDesc] = useState('');
  const [preview, setPreview] = useState<ReclassifyByAccountResult | null>(null);
  const [applyLog, setApplyLog] = useState<{ applied: number; failed: { id: string; reason: string }[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const accountLabel = useMemo(() => {
    const byId = new Map(accounts.map((a) => [a.id, `${a.code} ${a.name}`]));
    return (id: string) => byId.get(id) ?? id;
  }, [accounts]);

  const ready = !!fromAccountId && !!toAccountId && fromAccountId !== toAccountId && !!from && !!to && from <= to;

  const baseInput = () => ({
    fromAccountId,
    toAccountId,
    from,
    to,
    ...(descriptionContains.trim() ? { descriptionContains: descriptionContains.trim() } : {}),
    reasonCode: 'misclassified' as const,
    reasonNote: `Reallocations page: ${accountLabel(fromAccountId)} to ${accountLabel(toAccountId)}, ${from} to ${to}`,
  });

  const doPreview = async () => {
    setNote(null);
    setApplyLog(null);
    try {
      const res = await run.mutateAsync({ ...baseInput(), dryRun: true });
      setPreview(res);
    } catch (err) {
      setNote(errorText(err));
    }
  };

  const doApply = async () => {
    if (!preview) return;
    const count = preview.totals.candidateCount;
    const total = formatCurrency(preview.totals.candidateAmount);
    const ok = confirm(
      `Move ${count} transaction${count === 1 ? '' : 's'} totalling ${total} from ${accountLabel(fromAccountId)} to ${accountLabel(toAccountId)}? Amounts and reconciliations are not changed.`,
    );
    if (!ok) return;
    setBusy(true);
    setNote(null);
    const log = { applied: 0, failed: [] as { id: string; reason: string }[] };
    try {
      // The server caps one apply call; loop until it reports nothing left.
      // Rows that fail are excluded from the next pass by their own failure,
      // so the loop also stops when a pass applies nothing.
      for (let pass = 0; pass < 50; pass++) {
        const res = await run.mutateAsync({ ...baseInput(), dryRun: false });
        log.applied += res.applied;
        log.failed.push(...res.failed);
        if (res.remaining === 0 || res.applied === 0) break;
      }
      setApplyLog({ ...log });
      const after = await run.mutateAsync({ ...baseInput(), dryRun: true });
      setPreview(after);
    } catch (err) {
      setApplyLog({ ...log });
      setNote(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 12, maxWidth: 960 }}>
      <h2 style={{ fontSize: 14, marginTop: 0, marginBottom: 6 }}>Deposits and payments</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
        Every matched or reconciled bank transaction in the range whose other side sits on the
        from account moves to the to account. Only entries created by a bank match qualify;
        deposit documents, checks and split matches are listed as skipped.
      </p>
      <div className="form-grid" style={{ maxWidth: 720 }}>
        <label htmlFor="ra-from-acct">From account</label>
        <AccountPicker
          id="ra-from-acct"
          accounts={accounts}
          value={fromAccountId}
          onChange={setFrom}
          placeholder="Select the account the amounts are on now"
        />
        <label htmlFor="ra-to-acct">To account</label>
        <AccountPicker
          id="ra-to-acct"
          accounts={accounts}
          value={toAccountId}
          onChange={setTo}
          predicate={isPostable}
          placeholder="Select the account they belong on"
        />
        <label htmlFor="ra-from">From date</label>
        <input id="ra-from" type="date" value={from} onChange={(e) => setFromDate(e.target.value)} />
        <label htmlFor="ra-to">To date</label>
        <input id="ra-to" type="date" value={to} onChange={(e) => setToDate(e.target.value)} />
        <label htmlFor="ra-desc">Description contains</label>
        <input
          id="ra-desc"
          type="text"
          value={descriptionContains}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="Optional, for example SQUARE"
          maxLength={200}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        <button type="button" className="btn-secondary" disabled={!ready || run.isPending || busy} onClick={doPreview}>
          {run.isPending && !busy ? 'Previewing' : 'Preview'}
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={!preview || preview.totals.candidateCount === 0 || busy || run.isPending}
          onClick={doApply}
        >
          {busy ? 'Applying' : `Apply ${preview?.totals.candidateCount ?? 0}`}
        </button>
        {preview && preview.totals.candidateCount > RECLASSIFY_BY_ACCOUNT_MAX_APPLY && (
          <span className="muted" style={{ fontSize: 12 }}>
            Applies in passes of {RECLASSIFY_BY_ACCOUNT_MAX_APPLY}.
          </span>
        )}
      </div>
      {note && <div className="field-error" style={{ marginTop: 8 }}>{note}</div>}
      {applyLog && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <span className="tag ok">Applied {applyLog.applied}</span>{' '}
          {applyLog.failed.length > 0 && <span className="tag">Failed {applyLog.failed.length}</span>}
          {applyLog.failed.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {applyLog.failed.slice(0, 50).map((f) => (
                <li key={f.id}>
                  <code style={{ fontSize: 11 }}>{f.id.slice(0, 8)}</code> {f.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {preview && <ByAccountPreview preview={preview} />}
    </div>
  );
}

function ByAccountPreview({ preview }: { preview: ReclassifyByAccountResult }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>
        <strong>{preview.totals.candidateCount}</strong> to move, totalling{' '}
        <strong>{formatCurrency(preview.totals.candidateAmount)}</strong>.{' '}
        {preview.totals.skippedCount > 0 && <span className="muted">{preview.totals.skippedCount} skipped.</span>}
      </div>
      {preview.candidates.length > 0 && (
        <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Status</th>
                <th>Entry</th>
              </tr>
            </thead>
            <tbody>
              {preview.candidates.map((c) => (
                <tr key={c.id}>
                  <td>{formatAccountingDate(c.date)}</td>
                  <td>{c.description}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(c.amount)}</td>
                  <td>{c.matchStatus}</td>
                  <td className="muted">
                    {c.entryNumber}
                    {c.via === 'entry' && (
                      <span
                        className="tag info"
                        style={{ marginLeft: 6, fontSize: 11 }}
                        title={`Created by ${c.entrySource}; the entry itself is re-accounted, its bank line stays put`}
                      >
                        via entry
                      </span>
                    )}
                    {c.linked === false && (
                      <span
                        className="tag"
                        style={{ marginLeft: 6, fontSize: 11 }}
                        title="No bank transaction points at this entry; it is re-accounted on its own"
                      >
                        unlinked
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {preview.skipped.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13 }}>Skipped ({preview.skipped.length})</summary>
          <div style={{ overflowX: 'auto', maxHeight: 300, overflowY: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {preview.skipped.map((s) => (
                  <tr key={s.id}>
                    <td>{formatAccountingDate(s.date)}</td>
                    <td>{s.description}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(s.amount)}</td>
                    <td className="muted">{s.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}

// ── Payroll card tips ───────────────────────────────────────────────────────

function PayrollTipsPanel({ companyId, accounts }: { companyId: string; accounts: Account[] }) {
  const run = useReclassifyPayrollTips(companyId);
  const [from, setFromDate] = useState('');
  const [to, setToDate] = useState('');
  const [tipsAccountId, setTips] = useState('');
  const [preview, setPreview] = useState<ReclassifyPayrollTipsResult | null>(null);
  const [applied, setApplied] = useState<ReclassifyPayrollTipsResult | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Default the tips account to code 2025 when the chart has it.
  useEffect(() => {
    if (tipsAccountId) return;
    const hit = accounts.find((a) => a.code === TIPS_DEFAULT_CODE && isPostable(a));
    if (hit) setTips(hit.id);
  }, [accounts, tipsAccountId]);

  const ready = !!tipsAccountId && !!from && !!to && from <= to;

  const doPreview = async () => {
    setNote(null);
    setApplied(null);
    try {
      setPreview(await run.mutateAsync({ from, to, tipsAccountId, dryRun: true }));
    } catch (err) {
      setNote(errorText(err));
    }
  };

  const doApply = async () => {
    if (!preview) return;
    const n = preview.totals.planned;
    const ok = confirm(
      `Break ${formatCurrency(preview.totals.tipsTotal)} of card tips out of wages on ${n} paystub${n === 1 ? '' : 's'}? Each entry stays balanced and reconciled lines are not touched.`,
    );
    if (!ok) return;
    setNote(null);
    try {
      const res = await run.mutateAsync({ from, to, tipsAccountId, dryRun: false });
      setApplied(res);
      setPreview(await run.mutateAsync({ from, to, tipsAccountId, dryRun: true }));
    } catch (err) {
      setNote(errorText(err));
    }
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 12, maxWidth: 960 }}>
      <h2 style={{ fontSize: 14, marginTop: 0, marginBottom: 6 }}>Payroll card tips</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
        Each paystub's card tips are posted inside the wages debit. This splits them out onto the
        tips account, one entry per stub, and skips any stub already done.
      </p>
      <div className="form-grid" style={{ maxWidth: 720 }}>
        <label htmlFor="pt-from">Pay date from</label>
        <input id="pt-from" type="date" value={from} onChange={(e) => setFromDate(e.target.value)} />
        <label htmlFor="pt-to">Pay date to</label>
        <input id="pt-to" type="date" value={to} onChange={(e) => setToDate(e.target.value)} />
        <label htmlFor="pt-acct">Tips account</label>
        <AccountPicker
          id="pt-acct"
          accounts={accounts}
          value={tipsAccountId}
          onChange={setTips}
          predicate={isPostable}
          placeholder="Select the tips account"
        />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        <button type="button" className="btn-secondary" disabled={!ready || run.isPending} onClick={doPreview}>
          Preview
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={!preview || preview.totals.planned === 0 || run.isPending}
          onClick={doApply}
        >
          {run.isPending ? 'Working' : `Apply ${preview?.totals.planned ?? 0}`}
        </button>
      </div>
      {note && <div className="field-error" style={{ marginTop: 8 }}>{note}</div>}
      {applied && (
        <div style={{ marginTop: 8, fontSize: 13 }}>
          <span className="tag ok">Applied {applied.applied.length}</span>{' '}
          <span className="muted">Skipped {applied.skipped.length}</span>{' '}
          {applied.failed.length > 0 && <span className="tag">Failed {applied.failed.length}</span>}
          {applied.failed.length > 0 && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {applied.failed.map((f) => (
                <li key={f.paystubId}>
                  {f.entryNumber} {f.employeeName} {formatAccountingDate(f.payDate)}: {f.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {preview && <TipsPreview preview={preview} />}
    </div>
  );
}

function TipsPreview({ preview }: { preview: ReclassifyPayrollTipsResult }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 13, marginBottom: 6 }}>
        <strong>{preview.totals.planned}</strong> stub{preview.totals.planned === 1 ? '' : 's'} to change,
        tips totalling <strong>{formatCurrency(preview.totals.tipsTotal)}</strong>.{' '}
        {preview.totals.skippedCount > 0 && <span className="muted">{preview.totals.skippedCount} skipped.</span>}
      </div>
      {preview.plan.length > 0 && (
        <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Entry</th>
                <th>Pay date</th>
                <th>Employee</th>
                <th style={{ textAlign: 'right' }}>Wages before</th>
                <th style={{ textAlign: 'right' }}>Tips</th>
                <th style={{ textAlign: 'right' }}>Wages after</th>
              </tr>
            </thead>
            <tbody>
              {preview.plan.map((p) => (
                <tr key={p.paystubId}>
                  <td className="muted">{p.entryNumber}</td>
                  <td>{formatAccountingDate(p.payDate)}</td>
                  <td>{p.employeeName}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.wagesBefore)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.tips)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.wagesAfter)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {preview.skipped.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13 }}>Skipped ({preview.skipped.length})</summary>
          <table className="table">
            <thead>
              <tr>
                <th>Entry</th>
                <th>Pay date</th>
                <th>Employee</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {preview.skipped.map((s) => (
                <tr key={s.paystubId}>
                  <td className="muted">{s.entryNumber ?? ''}</td>
                  <td>{formatAccountingDate(s.payDate)}</td>
                  <td>{s.employeeName}</td>
                  <td className="muted">{s.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
